import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { fetchPublicChallenges, type PublicChallengeItem } from "@/features/public/api";
import { useRealtimeInvalidation } from "@/shared/realtime/useRealtimeInvalidation";

function formatDaysLeft(deadline: string) {
  const daysLeft = Math.ceil((new Date(`${deadline}T23:59:59`).getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  if (daysLeft <= 0) {
    return "Terminé";
  }

  return `${daysLeft}j restants`;
}

function resolveChallengeProgress(challenge: PublicChallengeItem) {
  const cumulativeScore = challenge.leaderboard.reduce((total, entry) => total + entry.score, 0);
  const percent = challenge.target > 0 ? Math.min(100, Math.round((cumulativeScore / challenge.target) * 100)) : 0;

  return {
    percent,
    score: cumulativeScore
  };
}

function isChallengeActive(deadline: string) {
  return new Date(`${deadline}T23:59:59`).getTime() >= Date.now();
}

export default function ChallengesSection() {
  const [visible, setVisible] = useState(false);
  const [challenges, setChallenges] = useState<PublicChallengeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const ref = useRef<HTMLDivElement>(null);

  const loadChallenges = useCallback(async () => {
    setLoading(true);

    try {
      const payload = await fetchPublicChallenges();
      setChallenges(
        payload
          .filter((challenge) => isChallengeActive(challenge.deadline))
          .sort((left, right) => new Date(left.deadline).getTime() - new Date(right.deadline).getTime())
          .slice(0, 3)
      );
    } catch {
      setChallenges([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    void loadChallenges();
  }, [loadChallenges]);

  useRealtimeInvalidation({
    topics: ["challenges"],
    onInvalidate: () => {
      void loadChallenges();
    }
  });

  return (
    <section ref={ref} className="py-24 bg-[#FAF9F7]">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between mb-12 gap-4 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full bg-teal-50 text-[#1A8C7A] text-sm font-medium mb-3">
              Défis
            </span>
            <h2 className="text-4xl font-bold text-[#1A1A1A]">Défis Actifs</h2>
          </div>
          <Link
            to="/social?tab=challenges"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#E8521A] hover:gap-3 transition-all whitespace-nowrap"
          >
            Voir tous les défis
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {loading
            ? Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="h-[360px] rounded-3xl border-2 border-gray-200 bg-white animate-pulse"></div>
              ))
            : challenges.length > 0
              ? challenges.map((challenge, index) => {
                  const progress = resolveChallengeProgress(challenge);

                  return (
                    <div
                      key={challenge.id}
                      className={`group relative rounded-3xl overflow-hidden border-2 border-gray-200 transition-all duration-300 hover:scale-[1.02] ${
                        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                      }`}
                      style={{ transitionDelay: `${index * 100}ms`, aspectRatio: "4/3" }}
                    >
                      {challenge.image ? (
                        <img
                          src={challenge.image}
                          alt={challenge.title}
                          className="h-full w-full object-cover object-top"
                        />
                      ) : (
                        <div className={`h-full w-full bg-gradient-to-br ${challenge.bgColor} from-white via-white to-transparent flex items-center justify-center`}>
                          <div className="flex h-24 w-24 items-center justify-center rounded-[28px] bg-white/75 shadow-sm">
                            <i className={`${challenge.icon} ${challenge.color} text-5xl`}></i>
                          </div>
                        </div>
                      )}

                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent group-hover:from-black/90 transition-all duration-300"></div>

                      <div className="absolute left-4 top-4">
                        <span className="rounded-full bg-white/20 px-3 py-1.5 text-sm font-medium text-white backdrop-blur-sm">
                          {formatDaysLeft(challenge.deadline)}
                        </span>
                      </div>

                      <div className="absolute right-4 top-4">
                        <span className="rounded-full bg-black/35 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-white backdrop-blur-sm">
                          {challenge.sport}
                        </span>
                      </div>

                        <div className="absolute bottom-0 left-0 right-0 p-6">
                        <span className="mb-2 block text-sm font-medium text-white/70">
                          {challenge.creatorName
                            ? challenge.createdBySquadLeader
                              ? `Défi de squad lancé par ${challenge.creatorName}`
                              : `Défi individuel lancé par ${challenge.creatorName}`
                            : "Défi communautaire"}
                        </span>
                        <h3 className="mb-3 text-xl font-bold leading-tight text-white">{challenge.title}</h3>

                        <div className="mb-4">
                          <div className="mb-1.5 flex justify-between text-sm text-white/75">
                            <span>{challenge.participants} participants</span>
                            <span>{progress.percent}%</span>
                          </div>
                          <div className="h-1.5 overflow-hidden rounded-full bg-white/20">
                            <div
                              className="h-full rounded-full bg-green-500 transition-all duration-500"
                              style={{ width: `${progress.percent}%` }}
                            ></div>
                          </div>
                        </div>

                        <div className="mb-4 text-sm text-white/75">
                          Objectif: {challenge.target.toLocaleString("fr-FR")} {challenge.unit}
                        </div>

                        <Link
                          to="/social?tab=challenges"
                          className="inline-flex items-center gap-2 rounded-full bg-[#E8521A] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#d44a16] whitespace-nowrap"
                        >
                          <i className="ri-eye-line"></i>
                          Voir le défi
                        </Link>
                      </div>
                    </div>
                  );
                })
              : (
                <div className="md:col-span-3 rounded-3xl border-2 border-dashed border-gray-200 bg-white px-6 py-14 text-center">
                  <i className="ri-trophy-line text-4xl text-gray-300"></i>
                  <h3 className="mt-4 text-lg font-semibold text-[#1A1A1A]">Aucun défi actif à afficher</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Les défis encore actifs créés par les membres apparaîtront ici automatiquement.
                  </p>
                </div>
              )}
        </div>
      </div>
    </section>
  );
}
